import React from 'react';
import { connect } from 'react-redux';
import { OverlayDrawer, DrawerHeader, DrawerHeaderTitle, DrawerBody, DrawerFooter, Button, Spinner, Text, Field, Input, Link, Subtitle1 } from '@fluentui/react-components';
import { Edit12Regular, CalendarToday28Regular, Dismiss24Regular } from '@fluentui/react-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// functions
import * as ComponentFunctions from './PractitionerConsumptionDetailsMini.Functions';
import { VisitTypeEnum } from '../../../../models/Enums/VisitTypeEnum';
import { RateTypeEnum } from '../../../../models/Enums/RateTypeEnum';
import { formatCurrencyAmount } from '../PractitionerConsumptionCurrency';
import { CurrencySelectorComponent } from '../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import PractitionerConsumptionRateDrawerComponent from '../../PractitionerConsumptionRateDrawer/PractitionerConsumptionRateDrawer.Component';
import { usePractitionerConsumptionDetailsMiniStyles } from './PractitionerConsumptionDetailsMini.Styles';
import { feeBreakdown, formatPercentage, hasFeeBreakdown } from '../../../../commons/fee/PractitionerFee.Logic';

const getVisitTypeLabel = (value: number | undefined) => {
  switch (value) {
    case VisitTypeEnum.IN_PERSON: return 'Presencial';
    case VisitTypeEnum.TELEHEALTH: return 'Remota';
    case VisitTypeEnum.HOME_VISIT: return 'Domicilio';
    case VisitTypeEnum.OTHER: return 'Otro';
    default: return '-';
  }
};

const getRateTypeLabel = (value: number | undefined) => {
  switch (value) {
    case RateTypeEnum.InvoiceDate: return 'Fecha de factura';
    case RateTypeEnum.PaymentDate: return 'Fecha de pago';
    case RateTypeEnum.Manual: return 'Manual';
    case RateTypeEnum.Automatic: return 'Automática';
    default: return '-';
  }
};

const PractitionerConsumptionDetailsMiniComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = usePractitionerConsumptionDetailsMiniStyles();

  // estado local para edición de monto
  const [isAmountEditorVisible, setIsAmountEditorVisible] = React.useState(false);
  const [isRateDrawerOpen, setIsRateDrawerOpen] = React.useState(false);
  const [newUnitAmount, setNewUnitAmount] = React.useState<number | undefined>(props.Consumption?.unitAmount);
  const [newCurrency, setNewCurrency] = React.useState<string>(props.Consumption?.currencyCode || 'USD');

  React.useEffect(() => {
    setNewUnitAmount(props.Consumption?.unitAmount);
    setNewCurrency(props.Consumption?.currencyCode || 'USD');
  }, [props.Consumption?.id]);

  const onSaveAmount = () => {
    if (!props.Consumption) return;

    const parsedAmount = Number(newUnitAmount);
    if (!Number.isFinite(parsedAmount)) return;

    const practitionerId = props.PractitionerId || props.Consumption.practitionerId;
    if (!practitionerId) return;

    props.ChangeAmount(practitionerId, props.Consumption.id, parsedAmount, newCurrency || props.Consumption.currencyCode || 'USD');
    setIsAmountEditorVisible(false);
  };

  return (
    <OverlayDrawer open={props.isOpen} onOpenChange={(_, data) => { if (!data.open) props.onClose(); }} position='end' size='medium'>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance='subtle'
              aria-label='Close'
              icon={<Dismiss24Regular />}
              onClick={() => props.onClose()}
            />
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CalendarToday28Regular />
            <div>
              <Subtitle1 style={{ textTransform: 'capitalize' }}>{props.Consumption?.serviceName || 'Detalle de consumo'}</Subtitle1>
              <Text size={200}>{props.Consumption?.stageName}</Text>
            </div>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {props.isLoading && (
          <div className={styles.spinnerWrapper}>
            <Spinner label='Cargando detalle...' />
          </div>
        )}

        {props.Consumption && !props.isLoading && (
          <div className={styles.body}>

            {/* ── Zona 1: Información general (solo lectura) ── */}
            <div className={styles.infoCard}>
              <Text className={styles.sectionTitle}>Información general</Text>
              <div className={styles.dataGrid}>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Paciente</Text>
                  <Text className={styles.dataValue}>
                    {props.Consumption.patientNumberInProtocol || '-'}
                    {props.Consumption.medicalRecordNumber && (
                      <span className={styles.dataValueMeta}> · HC {props.Consumption.medicalRecordNumber}</span>
                    )}
                  </Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Fecha de realización</Text>
                  <Text className={styles.dataValue}>
                    {props.Consumption.serviceDueDate ? moment(props.Consumption.serviceDueDate).format('DD/MM/YYYY HH:mm') : '-'}
                  </Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Visita</Text>
                  <Text className={styles.dataValue}>{props.Consumption.stageName || 'Sin visita asociada'}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Tipo de visita</Text>
                  <Text className={styles.dataValue}>{getVisitTypeLabel(props.Consumption.realizedType)}</Text>
                </div>
              </div>
              {props.Consumption.markedAsHaveWarnings && (
                <div className={styles.warningBox} role="note">
                  <Text weight="semibold" size={200} block>Advertencia</Text>
                  <Text size={200}>{(props.Consumption.warningsComments || '').trim() || 'Este consumo tiene una advertencia.'}</Text>
                </div>
              )}
              {props.Consumption?.settlementId && props.Consumption.status === 3 && (
                <div className={styles.settlementRow}>
                  <Text className={styles.dataLabel}>Liquidación</Text>
                  <Link href={'../settlements/' + props.Consumption.settlementId} target='_blank'>
                    {props.Consumption.settlementIdentifier || 'Ver liquidación'}
                  </Link>
                </div>
              )}
            </div>

            {/* ── Zona 2: Resumen financiero (editable) ── */}
            <div className={styles.editCard}>
              <div className={styles.cardHeader}>
                <Text className={styles.sectionTitle}>Resumen financiero</Text>
                <Button appearance='subtle' size='small' icon={<Edit12Regular />} onClick={() => setIsAmountEditorVisible((v) => !v)}>
                  {isAmountEditorVisible ? 'Ocultar editor' : 'Editar monto'}
                </Button>
              </div>

              {isAmountEditorVisible && (
                <div className={styles.amountEditorPanel}>
                  <Text className={styles.sectionTitle}>Editar monto unitario</Text>
                  <div className={styles.fieldFull}>
                    <Field label='Monto'>
                      <Input
                        style={{ width: '100%' }}
                        type='number'
                        step={0.01}
                        value={newUnitAmount?.toString() ?? ''}
                        onChange={(e) => {
                          const v = e.target.value === '' ? undefined : Number(e.target.value);
                          setNewUnitAmount(v);
                        }}
                      />
                    </Field>
                  </div>
                  <div className={styles.fieldFull}>
                    <CurrencySelectorComponent
                      label='Moneda'
                      isRequired={false}
                      errorMessage={undefined}
                      defaultSelected={newCurrency}
                      onCurrencyChange={(currency: string) => setNewCurrency(currency)}
                    />
                  </div>
                  <div className={styles.editorActions}>
                    <Button appearance='secondary' onClick={() => {
                      setIsAmountEditorVisible(false);
                      setNewUnitAmount(props.Consumption?.unitAmount);
                      setNewCurrency(props.Consumption?.currencyCode || 'USD');
                    }}>Cancelar</Button>
                    <Button
                      appearance='primary'
                      disabled={props.isSaving || newUnitAmount === undefined || Number.isNaN(newUnitAmount)}
                      onClick={onSaveAmount}
                    >
                      {props.isSaving && <Spinner size='extra-tiny' />} Guardar
                    </Button>
                  </div>
                </div>
              )}

              <div className={styles.dataGrid}>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Monto unitario</Text>
                  <Text className={styles.dataValueAccent}>{formatCurrencyAmount(props.Consumption.unitAmount, props.Consumption.currencyCode)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Total</Text>
                  <Text className={styles.dataValue}>{formatCurrencyAmount(props.Consumption.amount, props.Consumption.currencyCode)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>Pendiente</Text>
                  <Text className={styles.dataValue}>{formatCurrencyAmount(props.Consumption.pendingAmount, props.Consumption.currencyCode)}</Text>
                </div>
              </div>
            </div>

            {/* ── Zona 2b: De dónde sale el honorario ── */}
            {/* MedicalProtocol lo calcula sobre el consumo del protocolo que lo
                origina: facturado al sponsor − retención − costo = base × %. Sólo
                se muestra cuando el servidor informó esa base. */}
            {hasFeeBreakdown(props.Consumption) && (() => {
              const b = feeBreakdown(props.Consumption);
              const fmt = (value: number) => formatCurrencyAmount(value, props.Consumption?.currencyCode);
              return (
                <div className={styles.infoCard}>
                  <Text className={styles.sectionTitle}>Cómo se calcula el honorario</Text>
                  <table className={styles.feeTable}>
                    <tbody>
                      <tr>
                        <td className={styles.feeLabel}>Facturado al sponsor</td>
                        <td className={styles.feeAmount}>{fmt(b.source)}</td>
                      </tr>
                      <tr>
                        <td className={styles.feeLabel}>− Retención</td>
                        <td className={`${styles.feeAmount} ${styles.feeDeduct}`}>− {fmt(b.withholding)}</td>
                      </tr>
                      <tr>
                        <td className={styles.feeLabel}>− Costo descontado</td>
                        <td className={`${styles.feeAmount} ${styles.feeDeduct}`}>− {fmt(b.discountedCost)}</td>
                      </tr>
                      <tr className={styles.feeBaseRow}>
                        <td className={styles.feeLabel}>Base de cálculo</td>
                        <td className={styles.feeAmount}>{fmt(b.base)}</td>
                      </tr>
                      <tr>
                        <td className={styles.feeLabel}>× Porcentaje pactado</td>
                        <td className={styles.feeAmount}>{formatPercentage(b.percentage)}</td>
                      </tr>
                      <tr className={styles.feeResultRow}>
                        <td className={styles.feeLabel}>Honorario</td>
                        <td className={styles.feeAmount}>{fmt(b.fee)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {b.hasManualAdjustment && (
                    <Text size={200} className={styles.feeNote}>
                      El honorario no coincide con base × porcentaje: tiene un ajuste manual o una cotización aplicada.
                    </Text>
                  )}
                </div>
              );
            })()}

            {/* ── Zona 3: Cotización ── */}
            <div className={styles.resultCard}>
              <div className={styles.cardHeader}>
                <Text className={styles.sectionTitle}>{t('consumptionRateWorkflow.currentRate')}</Text>
                <Button appearance='subtle' size='small' icon={<Edit12Regular />} onClick={() => setIsRateDrawerOpen(true)}>
                  {t('consumptionRateWorkflow.editRate')}
                </Button>
              </div>
              <div className={styles.dataGrid}>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.currentRate')}</Text>
                  <Text className={styles.dataValueAccent}>{props.Consumption.rate ?? '-'}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.rateType')}</Text>
                  <Text className={styles.dataValue}>{ComponentFunctions.getRateTypeDescription(props.Consumption.rateType)}</Text>
                </div>
              </div>
            </div>

            {/* ── Zona 4: Snapshot de conversión ── */}
            <div className={styles.infoCard}>
              <Text className={styles.sectionTitle}>{t('conversionSnapshot.title')}</Text>
              <div className={styles.dataGrid}>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('conversionSnapshot.rate')}</Text>
                  <Text className={styles.dataValue}>{ComponentFunctions.formatSnapshotValue(props.Consumption.rate)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('conversionSnapshot.rateType')}</Text>
                  <Text className={styles.dataValue}>{getRateTypeLabel(props.Consumption.rateType)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('conversionSnapshot.originalCurrencyCode')}</Text>
                  <Text className={styles.dataValue}>{ComponentFunctions.formatSnapshotValue(props.Consumption.originalCurrencyCode)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('conversionSnapshot.originalUnitAmount')}</Text>
                  <Text className={styles.dataValue}>{ComponentFunctions.formatSnapshotValue(props.Consumption.originalUnitAmount)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('conversionSnapshot.originalAmount')}</Text>
                  <Text className={styles.dataValue}>{ComponentFunctions.formatSnapshotValue(props.Consumption.originalAmount)}</Text>
                </div>
                <div className={styles.dataItem}>
                  <Text className={styles.dataLabel}>{t('conversionSnapshot.relatedSettlement')}</Text>
                  <Text className={styles.dataValue}>{ComponentFunctions.formatSnapshotValue(props.Consumption.relatedSettlementName || props.Consumption.relatedSettlementIdentifier || props.Consumption.relatedSettlementId)}</Text>
                </div>
              </div>
            </div>

            {/* ── Zona 5: Comentarios (condicional) ── */}
            {(props.Consumption.conceptDescription || props.Consumption.comments || props.Consumption.moreInfoComments) && (
              <div className={styles.infoCard}>
                {props.Consumption.conceptDescription && (
                  <div className={styles.dataItem}>
                    <Text className={styles.dataLabel}>Concepto</Text>
                    <Text className={styles.dataValue}>{props.Consumption.conceptDescription}</Text>
                  </div>
                )}
                {(props.Consumption.comments || props.Consumption.moreInfoComments) && (
                  <div className={styles.dataItem}>
                    <Text className={styles.dataLabel}>Comentarios</Text>
                    <Text>{props.Consumption.comments || props.Consumption.moreInfoComments}</Text>
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </DrawerBody>

      <DrawerFooter>
        <div className={styles.footer}>
          <Button appearance='primary' onClick={() => props.onClose()}>{t('consumptionRateWorkflow.close')}</Button>
        </div>
      </DrawerFooter>

      {props.Consumption && props.PractitionerId && (
        <PractitionerConsumptionRateDrawerComponent
          PractitionerId={props.PractitionerId}
          ConsumptionId={props.Consumption.id}
          isOpen={isRateDrawerOpen}
          onClose={() => setIsRateDrawerOpen(false)}
        />
      )}
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PractitionerConsumptionDetailsMiniComponent as any);
